import { alpha, Box, IconButton, Radio, TextField } from '@mui/material'
// import { styled } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import { styled } from '../../styles/elements/debugStyled'

// ---------- helpers ----------
const bgHelp = (theme: Theme) => (theme.palette as any)?.background?.help ?? theme.palette.action.hover
const bgDisabled = (theme: Theme) =>
  (theme.palette as any)?.background?.disabled ?? theme.palette.action.disabledBackground
const bgDefaultDim = (theme: Theme) => (theme.palette as any)?.background?.divider ?? theme.palette.background.default

// ---------- plain objects (for sx use) ----------
export const dialogStyles = (theme: Theme) => ({
  width: '100%',
  maxWidth: '800px',
  color: theme.palette.text.primary,
  minHeight: '50vh',
  maxHeight: '95vh',
  padding: '30px',
  '& .MuiDialogContent-root': {
    width: '100%',
  },
  '& .MuiTextField-root': {
    margin: '10px 0',
  },
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.background.paper,
    minWidth: '100%',
  },
  '& .MuiFormControl-root': {
    width: '100%',
  },
  '& .MuiDateRangePicker-root': {
    width: '100%',
  },
  '&::-webkit-scrollbar-thumb': {
    // was: rgba(167,51,51,51) (invalid alpha). Use 0–1:
    backgroundColor: alpha(theme.palette.text.primary, 0.5),
    borderRadius: '6px',
  },
})

export const dialogTitleStyles = (theme: Theme) => ({
  color: theme.palette.text.primary,
  fontWeight: 'bold',
  textAlign: 'center',
})

export const boxStyles = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
}

// ---------- styled components ----------
export const DialogIcon = styled('img')({
  width: 50,
  height: 50,
  borderRadius: '50%',
})

export const StyledTaskTextField = styled(TextField)(({ theme }) => ({
  backgroundColor: 'transparent',
  textAlign: 'left',
  borderColor: alpha(theme.palette.text.primary, 0.12),
  borderWidth: '1px',
  borderStyle: 'solid',
  borderRadius: '4px',
  margin: 0,
  '&:hover': {
    borderColor: alpha(theme.palette.text.primary, 0.32),
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: alpha(theme.palette.text.primary, 0.12),
    },
    '&:hover fieldset': {
      borderWidth: '0px !important',
    },
    '&.Mui-focused fieldset': {
      borderWidth: '1px !important',
      borderColor: theme.palette.text.primary,
    },
  },
  '& .MuiOutlinedInput-input': {
    '&:focus': {
      backgroundColor: theme.palette.background.paper,
    },
  },
}))

export const RowContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '98%',
  flex: '1 1 auto',
  minWidth: 0,
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius * 1.5,
  marginBottom: theme.spacing(1),
  padding: theme.spacing(0.8),
  backgroundColor: 'transparent',
  transition: 'all 0.3s ease',
  '&[data-shrink]': {
    width: '75%',
  },
  '& .row-actions': {
    opacity: 0,
    pointerEvents: 'none',
    transform: 'translateY(-4px)',
    transition: 'opacity 120ms ease, transform 120ms ease',
  },

  '&[data-active="true"] .row-actions': {
    opacity: 1,
    pointerEvents: 'auto',
    transform: 'translateY(0)',
  },

  '&[data-pinned="true"]': {
    boxShadow: theme.shadows[4],
    backgroundColor: bgDefaultDim(theme),
    transform: 'scale(1.01)',
    borderColor: theme.palette.text.disabled,
    borderRadius: theme.shape.borderRadius * 1.5,
  },

  '&[data-suppress="true"]': {
    backgroundColor: bgDisabled(theme),
    zIndex: -1,
  },
}))

export const RowShell = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '1px', // numeric -> px in emotion; being explicit is fine
  position: 'relative',
  width: '100%',
})

export const OrgRowContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '98%',
  flex: '1 1 auto',
  minWidth: 0,
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius * 1.5,
  marginBottom: theme.spacing(1),
  padding: theme.spacing(0.8),
  backgroundColor: bgHelp(theme),

  '& .row-actions': {
    opacity: 0,
    pointerEvents: 'none',
    transform: 'translateY(-4px)',
    transition: 'opacity 120ms ease, transform 120ms ease',
  },

  '&[data-active="true"] .row-actions': {
    opacity: 1,
    pointerEvents: 'auto',
    transform: 'translateY(0)',
  },

  '&[data-pinned="true"]': {
    boxShadow: theme.shadows[2],
    borderStyle: 'solid',
  },

  '&[data-suppress="true"]': {
    backgroundColor: bgDisabled(theme),
  },
}))

export const DotsButton = styled(IconButton)({
  position: 'absolute',
  top: 12,
  right: -14,
  zIndex: 11,
  padding: 4,
  width: 32,
  height: 32,
  '&:hover': {
    backgroundColor: 'transparent',
  },
})

export const TaskRadio = styled(Radio)({
  zIndex: 3000,
  padding: 4,
  width: 32,
  height: 32,
  // `px` is not a CSS prop; if you meant paddingX, do paddingInline instead:
  // paddingInline: 4,
  '&:hover': {
    backgroundColor: 'transparent',
  },
})

// filter custom prop so it doesn’t end up on the DOM element
export const BoxRow = styled('div')(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  height: '100%',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '3px',
  '&[data-suppress="true"]': {
    filter: 'grayscale(100%)',
  },
}))
// export const BoxRow = styled('div', {
//   shouldForwardProp: prop => prop !== 'suppress',
// })<BoxRowProps>(ownerState => {
//   // quick sanity log:
//   if (import.meta.env.MODE === 'development') {
//     // eslint-disable-next-line no-console
//     console.debug('BoxRow styled callback', ownerState)
//   }
//   const { suppress } = ownerState
//   return {
//     display: 'flex',
//     height: '100%',
//     flexDirection: 'row',
//     alignItems: 'center',
//     filter: suppress ? 'grayscale(100%)' : undefined,
//     gap: '3px',
//   }
// })

export const RowActions = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 3,
  right: 3,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8.5px',
  backgroundColor: bgHelp(theme),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: theme.shadows[2],
  opacity: 0,
  pointerEvents: 'none',
  padding: '2px 4px',
  zIndex: 10,
  justifyContent: 'space-between',
  transform: 'translateY(-4px)',
  height: '100%',
  maxHeight: 40,
  transition: 'opacity 120ms ease, transform 120ms ease',
}))

export const RowContent = styled(Box)({
  paddingRight: 'var(--actions-w)',
  transition: 'padding-right 140ms ease',
})
export const denseInput = {
  '& .MuiInputBase-root': { minHeight: 40, maxHeight: 40 },
  '& .MuiOutlinedInput-input': { py: 0.5 },
  '& .MuiOutlinedInput-root': { maxHeight: 40 },
  '& .MuiFormHelperText-root': { mt: 0.25 },
}
export const compactSelectSx = {
  minWidth: 0,
  '& .MuiInputLabel-root': {
    fontSize: 12,
    transform: 'translate(6px, 11px) scale(1)', // nicer small start
    '&.MuiInputLabel-shrink': { transform: 'translate(0px, 0px) scale(0.85)' },
  },
  '& .MuiOutlinedInput-root': {
    height: 36, // ~TextField small
    '& .MuiSelect-select': { py: 0.25 }, // tighter vertical padding
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    fontSize: 10, // small floating label notch
  },
} as const
export const compactInputSx = {
  minWidth: 0,
  // Label tweaks
  '& .MuiInputLabel-root': {
    fontSize: 12,
    transform: 'translate(6px, 11px) scale(1)',
    '&.MuiInputLabel-shrink': { transform: 'translate(0px, 0px) scale(0.85)' },
  },

  // Outlined / standard inputs
  '& .MuiOutlinedInput-root, & .MuiInputBase-root': {
    height: 36,
    fontSize: 13,
    '& input': { py: 0.5 }, // for TextField
    '& .MuiSelect-select': { py: 0.25 }, // for Select
  },

  '& .MuiOutlinedInput-notchedOutline legend': {
    fontSize: 10,
  },

  '& .MuiFormHelperText-root': {
    mt: 0.25,
    fontSize: 11,
  },
} as const
